import type { SlotName } from "@/lib/ads/zones";
import type { AdvertiserSlot } from "@/lib/mayar/products";

/**
 * A zone a paid campaign can render in: every `AdSlot` slot, plus `hunt` —
 * the sponsored card under the Hunt results list (`HuntSponsoredCard`). Hunt
 * is not an `AdSlot` (no AdSense, no reserved slot dimensions), so it is a
 * paid-campaign zone without being a `SlotName`.
 */
export type PaidCampaignZone = SlotName | "hunt";

/**
 * Which zones each advertiser product slot fulfills. The "site-wide" bundle
 * covers every zone; the single placements map to their own zone(s). Used to
 * decide whether an active paid campaign should render in a given zone.
 */
const ADVERTISER_SLOT_TO_ZONES: Record<AdvertiserSlot, PaidCampaignZone[]> = {
	"feed-sidebar": ["feed-sidebar"],
	blog: ["blog-top", "blog-bottom"],
	"category-footer": ["category-footer"],
	"site-wide": [
		"blog-top",
		"blog-bottom",
		"feed-sidebar",
		"category-footer",
		"job-detail-bottom",
		"job-detail-mid",
		"feed-inline-display",
		"hunt",
	],
};

/** Minimal active-campaign shape the AdSlot needs to render a paid creative. */
export type PaidCampaignView = {
	id: string;
	slot: string; // AdvertiserSlot
	localeTarget: string; // id | en | all
	headline: string;
	body: string;
	ctaText: string;
	ctaUrl: string;
	accent: string;
};

function zonesForSlot(slot: string): PaidCampaignZone[] {
	return ADVERTISER_SLOT_TO_ZONES[slot as AdvertiserSlot] ?? [];
}

/**
 * Pick the active paid campaign to render in a given AdSlot zone, or null.
 * Matches on (a) the zone being covered by the campaign's product slot and
 * (b) locale targeting (`all` or the page locale). Geo/market is intentionally
 * NOT gated here — the host pages are ISR/edge-cached and market is resolved
 * client-side; locale is the server-known proxy (id ≈ Indonesia audience).
 * When several match they rotate: stable id order, index advanced every
 * 15 minutes (`ROTATION_WINDOW_MS`) and offset per zone, so flat "all
 * placements" advertisers share inventory instead of the oldest one winning
 * forever. The host pages are cached, so rotation is per cache window, not
 * per request.
 */
export const ROTATION_WINDOW_MS = 900_000;

function fnv(s: string): number {
	let h = 0x811c9dc5;
	for (let i = 0; i < s.length; i++) {
		h ^= s.charCodeAt(i);
		h = Math.imul(h, 0x01000193) >>> 0;
	}
	return h >>> 0;
}

export function pickPaidCampaignForZone(
	campaigns: PaidCampaignView[],
	zone: PaidCampaignZone,
	locale: string,
	nowMs: number = Date.now(),
): PaidCampaignView | null {
	const matches = campaigns
		.filter(
			(c) =>
				zonesForSlot(c.slot).includes(zone) &&
				(c.localeTarget === "all" || c.localeTarget === locale),
		)
		.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
	if (matches.length === 0) return null;
	const idx =
		(fnv(zone) + Math.floor(nowMs / ROTATION_WINDOW_MS)) % matches.length;
	return matches[idx] ?? null;
}

/** Stable per-campaign tracking platform id (joins impression→click in D1). */
export function paidCampaignPlatform(campaignId: string): string {
	return `paid:${campaignId}`;
}
